import _ from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Box, Grid, InputLabel as MuiInputLabel, Stack, styled } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import colors from '@/assets/scss/_themes-vars.module.scss'

import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import TextInputController from '@/components/ui/inputs/TextInputController'
import TransferList from '@/components/ui/lists/TransferList'

import { ExternalService, externalServices } from '@/constants/external-services'

import PolicySelectionTable, { PolicyData } from '@/pages/admin/groups/components/PolicySelectionTable'

import { PartialDashboard, SidebarType } from '@/utils/subscription/types'

const licenseFormSchema = z.object({
  components: z.array(z.string()),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
  name: z.string().min(1),
  policies: z.array(z.string()),
  services: z.array(z.string()),
})

export type LicenseFormValues = z.infer<typeof licenseFormSchema>

export type LicenseFormProps = {
  initialValues?: LicenseFormValues
  isSubmitting?: boolean
  onSubmit: (values: LicenseFormValues) => void
  policies?: PolicyData[]
  readOnly?: boolean
}

const InputLabel = styled(MuiInputLabel)({
  color: colors.gray700,
  fontSize: '0.875rem',
  fontWeight: 500,
  transform: 'none',
})

export default function LicenseForm(props: LicenseFormProps) {
  const { initialValues, isSubmitting = false, onSubmit, policies = [], readOnly } = props
  const { t } = useTranslation()

  const { control, handleSubmit, setValue } = useForm<LicenseFormValues>({
    defaultValues: initialValues ?? { components: [], description: '', isDefault: false, name: '', policies: [], services: [] },
    resolver: zodResolver(licenseFormSchema),
  })

  const dashboardComponentOptions = useMemo(
    () =>
      Object.keys(PartialDashboard).map((key) => ({
        label: t(`common:licenseComponent.dashboard.element.${PartialDashboard[key as keyof typeof PartialDashboard]}`),
        value: `dashboard:${PartialDashboard[key as keyof typeof PartialDashboard]}`,
      })),
    [t]
  )

  const sidebarComponentOptions = useMemo(
    () =>
      Object.keys(SidebarType).map((key) => ({
        label:
          SidebarType[key as keyof typeof SidebarType] === SidebarType.AdminAirtable
            ? 'Airtable'
            : t(`common:licenseComponent.sidebar.element.${SidebarType[key as keyof typeof SidebarType].split(':').join('-')}`),
        value: `sidebar:${SidebarType[key as keyof typeof SidebarType]}`,
      })),
    [t]
  )

  const serviceOptions = useMemo(
    () =>
      externalServices.map((service) => ({
        label: service === ExternalService.airtable ? 'Airtable' : t(`common:externalService.${service}.label`),
        value: service,
      })),
    [t]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container rowGap={3} sx={{ mt: '0' }}>
        <Grid item xs={12} md={6}>
          <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={readOnly} />
        </Grid>
        <Grid item xs={12}>
          <TextInputController
            control={control}
            fullWidth
            label={t('field.description.label')}
            name="description"
            multiline
            minRows={3}
            type="textarea"
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="isDefault"
            render={({ field }) => (
              <CheckboxInput
                checked={field.value}
                label={t('form.license.isDefaultField.label')}
                name={field.name}
                onChange={field.onChange}
                readOnly={readOnly}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <InputLabel>{t('field.componentSelection.label', { component: 'Dashboard' })}</InputLabel>
            <Controller
              control={control}
              name="components"
              render={({ field }) => (
                <TransferList
                  initialLeftOptions={dashboardComponentOptions}
                  initialRightOptions={_.filter(dashboardComponentOptions, (opt) => !!initialValues?.components.includes(opt.value))}
                  onChange={(options) => {
                    const sidebarComponentsValue = _.filter(field.value, (v) => _.startsWith(v, 'sidebar:'))
                    setValue('components', _.map(options, (option) => option.value).concat(sidebarComponentsValue))
                  }}
                  readOnly={readOnly}
                />
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <InputLabel>{t('field.componentSelection.label', { component: 'Sidebar' })}</InputLabel>
            <Controller
              control={control}
              name="components"
              render={({ field }) => (
                <TransferList
                  initialLeftOptions={sidebarComponentOptions}
                  initialRightOptions={_.compact(
                    _.filter(initialValues?.components, (component: string) => component.startsWith('sidebar:')).map(
                      (component) => _.find(sidebarComponentOptions, ['value', component]) ?? null
                    )
                  )}
                  onChange={(options) => {
                    const dashboardComponentsValue = _.filter(field.value, (v) => _.startsWith(v, 'dashboard:'))
                    setValue('components', dashboardComponentsValue.concat(_.map(options, (option) => option.value)))
                  }}
                  readOnly={readOnly}
                  sortable
                />
              )}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <InputLabel>{t('field.services.label')}</InputLabel>
            <TransferList
              initialLeftOptions={serviceOptions}
              initialRightOptions={_.filter(serviceOptions, (opt) => !!initialValues?.services.includes(opt.value))}
              onChange={(options) => {
                setValue(
                  'services',
                  _.map(options, (option) => option.value)
                )
              }}
              readOnly={readOnly}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Box border="1px solid #e0e0e0" borderRadius="4px">
            <Controller
              control={control}
              name="policies"
              render={({ field }) => (
                <PolicySelectionTable
                  data={policies}
                  onSelectRow={(rows) => {
                    setValue(
                      'policies',
                      rows.map((row) => row.id)
                    )
                  }}
                  selectedRows={policies.filter((policy) => field.value.includes(policy.id))}
                  selectable={!readOnly}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid container item xs={12} justifyContent="end">
          {!readOnly && (
            <LoadingButton loading={isSubmitting} type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          )}
        </Grid>
      </Grid>
    </form>
  )
}
